import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { MapPin, Phone, Mail, Clock, Send, MessageSquare, Globe, Award } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';

const ContactPage = () => {
  const location = useLocation();
  const preSelectedService = location.state?.preSelectedService;

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    destination: '',
    travelDateFrom: '',
    travelDateTo: '',
    travelers: '',
    budget: '',
    inquiryType: '',
    message: '',
    newsletter: false
  });

  // Pre-fill form based on selected service or quick search data
  useEffect(() => {
    const prefillData = location.state?.prefillData;
    
    if (preSelectedService) {
      const serviceMessages = {
        'custom-itineraries': 'I would like to learn more about custom itinerary planning services.',
        'visa-assistance': 'I need assistance with visa applications and documentation.',
        'luxury-accommodations': 'I am interested in luxury accommodation booking services.',
        'transportation': 'I need help with flight bookings and ground transportation arrangements.',
        'travel-consultancy': 'I would like to speak with a travel consultant for expert advice.',
        'group-travel': 'I am planning group travel and need specialized assistance.',
        'corporate-travel': 'I am interested in corporate travel management services for my business.'
      };

      setFormData(prev => ({
        ...prev,
        inquiryType: preSelectedService === 'corporate-travel' ? 'business' : 'vacation',
        message: serviceMessages[preSelectedService] || 'I would like to learn more about your services.'
      }));
    }

    // If there's prefill data from quick search, use it
    if (prefillData) {
      setFormData(prev => ({
        ...prev,
        ...prefillData
      }));
    }
  }, [preSelectedService, location.state]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Handle form submission
    console.log('Form submitted:', formData);
    // In a real app, this would send data to your backend
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-[#6C30ED]/10 via-white to-[#00DF98]/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
            Get In
            <span className="block bg-gradient-to-r from-[#6C30ED] to-[#00DF98] bg-clip-text text-transparent">
              Touch
            </span>
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed mb-12">
            Ready to start planning your extraordinary journey? Our travel experts are here to help 
            you create the perfect itinerary tailored to your dreams and preferences.
          </p>
          
          {/* Quick contact options */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div className="flex items-center justify-center p-4 bg-white/70 rounded-lg border border-gray-200">
              <Phone size={20} className="text-[#6C30ED] mr-3" />
              <div>
                <div className="font-semibold text-gray-900">Call Us</div>
                <div className="text-sm text-gray-600">+91 7400488233</div>
              </div>
            </div>
            <div className="flex items-center justify-center p-4 bg-white/70 rounded-lg border border-gray-200">
              <Mail size={20} className="text-[#00DF98] mr-3" />
              <div>
                <div className="font-semibold text-gray-900">Email Us</div>
                <div className="text-sm text-gray-600">sales@triptotravels.org</div>
              </div>
            </div>
            <div className="flex items-center justify-center p-4 bg-white/70 rounded-lg border border-gray-200">
              <MessageSquare size={20} className="text-[#4E8EFF] mr-3" />
              <div>
                <div className="font-semibold text-gray-900">Live Chat</div>
                <div className="text-sm text-gray-600">Available 24/7</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form and Info */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Contact Form */}
            <div className="lg:col-span-2">
              <Card className="shadow-xl border-0 bg-white">
                <CardHeader className="bg-gradient-to-r from-[#6C30ED]/5 to-[#4E8EFF]/5">
                  <CardTitle className="text-2xl font-bold text-gray-900">
                    {preSelectedService ? 'Service Inquiry' : 'Plan Your Dream Trip'}
                  </CardTitle>
                  <p className="text-gray-600">
                    {preSelectedService 
                      ? 'Get detailed information about our services and pricing.'
                      : 'Fill out the form below and our travel experts will get back to you within 24 hours.'
                    }
                  </p>
                  {preSelectedService && (
                    <div className="mt-4 p-3 bg-[#6C30ED]/10 rounded-lg border border-[#6C30ED]/20">
                      <p className="text-sm text-[#6C30ED] font-medium">
                        📋 Service Inquiry: {preSelectedService.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </p>
                    </div>
                  )}
                </CardHeader>
                <CardContent className="p-8">
                  <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Personal Information */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="firstName">First Name *</Label>
                        <Input
                          id="firstName"
                          value={formData.firstName}
                          onChange={(e) => handleInputChange('firstName', e.target.value)}
                          className="mt-1"
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="lastName">Last Name *</Label>
                        <Input
                          id="lastName"
                          value={formData.lastName}
                          onChange={(e) => handleInputChange('lastName', e.target.value)}
                          className="mt-1"
                          required
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="email">Email Address *</Label>
                        <Input
                          id="email"
                          type="email"
                          value={formData.email}
                          onChange={(e) => handleInputChange('email', e.target.value)}
                          className="mt-1"
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="phone">Phone Number</Label>
                        <Input
                          id="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={(e) => handleInputChange('phone', e.target.value)}
                          className="mt-1"
                        />
                      </div>
                    </div>

                    {/* Travel Details */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="destination">Preferred Destination</Label>
                        <Select onValueChange={(value) => handleInputChange('destination', value)}>
                          <SelectTrigger className="mt-1">
                            <SelectValue placeholder="Select destination" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="dubai">Dubai</SelectItem>
                            <SelectItem value="thailand">Thailand</SelectItem>
                            <SelectItem value="maldives">Maldives</SelectItem>
                            <SelectItem value="bali">Bali</SelectItem>
                            <SelectItem value="vietnam">Vietnam</SelectItem>
                            <SelectItem value="sri-lanka">Sri Lanka</SelectItem>
                            <SelectItem value="nepal">Nepal</SelectItem>
                            <SelectItem value="united-kingdom">United Kingdom</SelectItem>
                            <SelectItem value="multiple">Multiple Destinations</SelectItem>
                            <SelectItem value="flexible">I'm Flexible</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label htmlFor="travelDates">Travel Dates</Label>
                        <div className="grid grid-cols-2 gap-2 mt-1">
                          <div>
                            <Label htmlFor="travelDateFrom" className="text-sm text-gray-500">From</Label>
                            <Input
                              id="travelDateFrom"
                              type="date"
                              value={formData.travelDateFrom}
                              onChange={(e) => handleInputChange('travelDateFrom', e.target.value)}
                              className="mt-1"
                            />
                          </div>
                          <div>
                            <Label htmlFor="travelDateTo" className="text-sm text-gray-500">To</Label>
                            <Input
                              id="travelDateTo"
                              type="date"
                              value={formData.travelDateTo}
                              onChange={(e) => handleInputChange('travelDateTo', e.target.value)}
                              className="mt-1"
                              min={formData.travelDateFrom}
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="travelers">Number of Travelers</Label>
                        <Select onValueChange={(value) => handleInputChange('travelers', value)}>
                          <SelectTrigger className="mt-1">
                            <SelectValue placeholder="Select travelers" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="1">1 Traveler</SelectItem>
                            <SelectItem value="2">2 Travelers</SelectItem>
                            <SelectItem value="3-4">3-4 Travelers</SelectItem>
                            <SelectItem value="5-8">5-8 Travelers</SelectItem>
                            <SelectItem value="9+">9+ Travelers</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label htmlFor="budget">Budget Range (per person)</Label>
                        <Select onValueChange={(value) => handleInputChange('budget', value)}>
                          <SelectTrigger className="mt-1">
                            <SelectValue placeholder="Select budget" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="under-150000">Under ₹1,50,000</SelectItem>
                            <SelectItem value="150000-400000">₹1,50,000 - ₹4,00,000</SelectItem>
                            <SelectItem value="400000-800000">₹4,00,000 - ₹8,00,000</SelectItem>
                            <SelectItem value="800000+">₹8,00,000+</SelectItem>
                            <SelectItem value="flexible">I'm Flexible</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="inquiryType">Type of Inquiry</Label>
                      <Select onValueChange={(value) => handleInputChange('inquiryType', value)}>
                        <SelectTrigger className="mt-1">
                          <SelectValue placeholder="Select inquiry type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="vacation">Vacation Planning</SelectItem>
                          <SelectItem value="honeymoon">Honeymoon</SelectItem>
                          <SelectItem value="business">Business Travel</SelectItem>
                          <SelectItem value="group">Group Travel</SelectItem>
                          <SelectItem value="b2b">B2B Partnership</SelectItem>
                          <SelectItem value="general">General Inquiry</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label htmlFor="message">Tell us about your dream trip *</Label>
                      <Textarea
                        id="message"
                        value={formData.message}
                        onChange={(e) => handleInputChange('message', e.target.value)}
                        placeholder="Describe your travel preferences, interests, special requirements, or any questions you have..."
                        className="mt-1 min-h-[120px]"
                        required
                      />
                    </div>

                    {/* Newsletter Checkbox */}
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="newsletter"
                        checked={formData.newsletter}
                        onCheckedChange={(checked) => handleInputChange('newsletter', checked)}
                      />
                      <Label htmlFor="newsletter" className="text-sm text-gray-600">
                        I'd like to receive travel tips and exclusive offers via email
                      </Label>
                    </div>

                    <Button 
                      type="submit"
                      size="lg"
                      className="w-full bg-gradient-to-r from-[#FE4B79] to-[#6C30ED] hover:from-[#fe3667] hover:to-[#5c28d4] text-white font-semibold py-4 shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300"
                    >
                      Send My Inquiry
                      <Send size={18} className="ml-2" />
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>

            {/* Contact Information */}
            <div className="space-y-6">
              {/* Contact Details */}
              <Card className="shadow-lg border-0 bg-white">
                <CardHeader>
                  <CardTitle className="text-xl font-bold text-gray-900">
                    Contact Information
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="flex items-start">
                    <MapPin size={20} className="text-[#6C30ED] mr-4 mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Address</h4>
                      <p className="text-gray-600">
                        123 Travel Street<br />
                        Tourism District<br />
                        New York, NY 10001<br />
                        United States
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <Phone size={20} className="text-[#00DF98] mr-4 mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Phone</h4>
                      <p className="text-gray-600">+91 7400488233</p>
                      <p className="text-sm text-gray-500">Customer Service Available</p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <Mail size={20} className="text-[#4E8EFF] mr-4 mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Email</h4>
                      <p className="text-gray-600">sales@triptotravels.org</p>
                      <p className="text-sm text-gray-500">Response within 24 hours</p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <Clock size={20} className="text-[#FE4B79] mr-4 mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Business Hours</h4>
                      <div className="text-gray-600 text-sm space-y-1">
                        <p>Monday - Friday: 9:00 AM - 7:00 PM IST</p>
                        <p>Saturday: 10:00 AM - 3:30 PM IST</p>
                        <p>Sunday: Closed</p>
                        <p className="text-[#6C30ED] font-medium">Customer Service Available</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Why Contact Us */}
              <Card className="shadow-lg border-0 bg-gradient-to-br from-[#6C30ED]/5 to-[#4E8EFF]/5">
                <CardHeader>
                  <CardTitle className="text-xl font-bold text-gray-900">
                    Why Contact TRIPTO?
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center">
                    <Award size={16} className="text-[#00DF98] mr-3 flex-shrink-0" />
                    <span className="text-gray-700 text-sm">15+ years of travel expertise</span>
                  </div>
                  <div className="flex items-center">
                    <Globe size={16} className="text-[#4E8EFF] mr-3 flex-shrink-0" />
                    <span className="text-gray-700 text-sm">Personalized itineraries for every traveler</span>
                  </div>
                  <div className="flex items-center">
                    <MessageSquare size={16} className="text-[#FE4B79] mr-3 flex-shrink-0" />
                    <span className="text-gray-700 text-sm">24/7 support during your journey</span>
                  </div>
                  <div className="flex items-center">
                    <Phone size={16} className="text-[#6C30ED] mr-3 flex-shrink-0" />
                    <span className="text-gray-700 text-sm">Free consultation and trip planning</span>
                  </div>
                </CardContent>
              </Card>

              {/* Emergency Contact */}
              <Card className="shadow-lg border-0 bg-red-50 border-red-200">
                <CardHeader>
                  <CardTitle className="text-lg font-bold text-red-800">
                    Emergency Contact
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-red-700 text-sm mb-2">
                    For travelers currently on a TRIPTO journey who need immediate assistance:
                  </p>
                  <div className="font-semibold text-red-800">
                    📞 Emergency Hotline: +91 7400488233
                  </div>
                  <p className="text-xs text-red-600 mt-2">Available during business hours</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              Visit Our Office
            </h2>
            <p className="text-xl text-gray-600">
              Drop by for a face-to-face consultation with our travel experts.
            </p>
          </div>
          
          {/* Placeholder for map - in a real app, you'd use Google Maps or similar */}
          <div className="relative h-96 bg-gray-300 rounded-2xl overflow-hidden shadow-lg">
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center">
                <MapPin size={48} className="text-gray-600 mx-auto mb-4" />
                <p className="text-gray-600 font-medium">Interactive Map</p>
                <p className="text-gray-500 text-sm">123 Travel Street, Tourism District</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              Frequently Asked Questions
            </h2>
          </div>

          <div className="space-y-6">
            {[
              {
                question: "How far in advance should I contact you to plan my trip?",
                answer: "We recommend contacting us at least 2-3 months before your desired travel dates for international destinations. However, we can also help with last-minute bookings based on availability."
              },
              {
                question: "Do you charge consultation fees?",
                answer: "Initial consultations are completely free. We only charge when you book a trip with us, and our fees are transparent with no hidden costs."
              },
              {
                question: "Can you help with visa requirements?",
                answer: "Yes! We provide comprehensive visa assistance including document preparation, application guidance, and tracking throughout the process."
              },
              {
                question: "What if I need to make changes to my itinerary?",
                answer: "We understand that travel plans can change. We'll work with you to modify your itinerary, though some changes may incur fees depending on supplier policies."
              },
              {
                question: "Do you provide 24/7 support during travel?",
                answer: "Absolutely! All our travelers receive 24/7 emergency support throughout their journey for peace of mind."
              }
            ].map((faq, index) => (
              <Card key={index} className="shadow-md border-0">
                <CardContent className="p-6">
                  <h4 className="font-semibold text-gray-900 mb-3">{faq.question}</h4>
                  <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;